import React, { useEffect, useState } from "react";
import { getStudentExerciseOverview } from "../../../../services/authService";

const TREND_CONFIG = {
	progression: { icon: "↗", color: "#4ade80", label: "Progression" },
	regression: { icon: "↘", color: "#ef4444", label: "Régression" },
	new: { icon: "★", color: "#3498db", label: "Nouveau" },
	stable: { icon: "→", color: "rgba(255,255,255,0.4)", label: "Stable" },
};

function formatLastDate(dateStr) {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) return "";
	const today = new Date();
	const diffMs = today - date;
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	if (diffDays <= 0) return "aujourd'hui";
	if (diffDays === 1) return "hier";
	if (diffDays < 7) return `il y a ${diffDays} jours`;
	return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

function formatPerf(poids, reps) {
	if (poids != null && poids > 0) return `${poids}kg × ${reps}`;
	if (reps != null) return `${reps} reps`;
	return "—";
}

export default function StatsPanel({ studentId, editorTraining }) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!studentId) {
			setData(null);
			return;
		}
		setLoading(true);
		getStudentExerciseOverview(studentId, 30)
			.then((res) => {
				setData(res.success ? res.exercises : null);
			})
			.finally(() => setLoading(false));
	}, [studentId]);

	// Filter to only exercises present in current training
	const currentExerciseNames = new Set(
		(editorTraining?.exercices || [])
			.filter((ex) => ex.supprime !== 1)
			.map((ex) => (ex.nom || "").toLowerCase())
	);

	const inTraining = (data || []).filter((ex) =>
		currentExerciseNames.has((ex.nomExercice || "").toLowerCase())
	);
	const others = (data || []).filter(
		(ex) => !currentExerciseNames.has((ex.nomExercice || "").toLowerCase())
	);

	const renderItem = (ex) => {
		const trend = TREND_CONFIG[ex.trend] || TREND_CONFIG.stable;
		const lastPerf = formatPerf(ex.lastPoids, ex.lastReps);
		const prevPerf = ex.prevPoids != null || ex.prevReps != null ? formatPerf(ex.prevPoids, ex.prevReps) : null;
		return (
			<div key={ex.id || ex.nomExercice} className="editor-panel-stats-item">
				<div className="editor-panel-stats-item-header">
					<span className="editor-panel-stats-item-name">{ex.nomExercice}</span>
					<span className="editor-panel-stats-item-trend" style={{ color: trend.color }}>
						{trend.icon}
					</span>
				</div>
				<div className="editor-panel-stats-item-detail">
					<span className="editor-panel-stats-item-perf">{lastPerf}</span>
					{prevPerf && (
						<span className="editor-panel-stats-item-prev">prec : {prevPerf}</span>
					)}
				</div>
				<div className="editor-panel-stats-item-meta">
					{formatLastDate(ex.lastDate)} · {ex.sessionCount || 0} séance{(ex.sessionCount || 0) > 1 ? "s" : ""}
				</div>
			</div>
		);
	};

	if (!studentId) {
		return (
			<div className="editor-panel-stats-empty">
				Sélectionne un élève pour voir ses stats.
			</div>
		);
	}

	if (loading) {
		return <div className="editor-panel-stats-empty">Chargement…</div>;
	}

	if (!data || data.length === 0) {
		return (
			<div className="editor-panel-stats-empty">
				Aucune donnée sur les 30 derniers jours.
			</div>
		);
	}

	return (
		<div className="editor-panel-stats">
			{inTraining.length > 0 && (
				<>
					<div className="editor-panel-stats-section-title">Dans la séance courante</div>
					<div className="editor-panel-stats-list">
						{inTraining.map(renderItem)}
					</div>
				</>
			)}
			{others.length > 0 && (
				<>
					<div className="editor-panel-stats-section-title">Autres exercices récents</div>
					<div className="editor-panel-stats-list">
						{others.slice(0, 15).map(renderItem)}
					</div>
				</>
			)}
		</div>
	);
}
